/**
 * Why V2 was created:
 * 
 * While the original version of the DatePicker does support editing the value with keyboard, it is very clunky, and there is no built-in validation.
 * MUI 7 provides a DatePicker with improved keyboard editing capabilities and configurable validation straight out of the box.
 * In order to support incremental upgrade of the existing DatePicker components in the codebase, a separate version V2 was created alongside V1.
 */

import React, { useCallback, useState } from 'react'
import { isValid } from 'date-fns'
import {
  LocalizationProvider,
  DatePicker as MuiDatePicker,
  PickersDay,
  PickersDayProps,
  DateValidationError,
} from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { dateFnsLocale, localeDateFormat } from '../../../utils/localeUtils'
import theme from '../../../theme/theme'
import { formatDateWithLocale } from '../../../utils/date-utils'

interface DatePickerProps {
  label: string
  value: Date | null
  onChange: (selectedDate: Date | null, keyboardInputValue?: string) => void
  onError?: (error: DateValidationError | null) => void
  required?: boolean
  disabled?: boolean
  daysToDisable?: number[]
  thresholdDates?: Date[]
  maxDate?: Date | undefined
  minDate?: Date | undefined
  disablePast?: boolean
  disabledDates?: string[]
  dataTestId?: string
}

// Styling constants
const DAY_STYLES = {
  base: {
    '&.Mui-disabled:not(.Mui-selected)': {
      color: 'rgba(0,0,0,0.2)',
    },
    '&.MuiPickersDay-today.MuiButtonBase-root:not(.Mui-selected)': {
      border: '1px solid rgba(32,197,160,0.3)',
      backgroundColor: 'rgba(32,197,160,0.3)',
    },
  },
  threshold: {
    backgroundColor: theme.palette.error.contrastText,
  },
}

const INVALID_DATE_ERROR_MESSAGE = 'Your date is not valid'

function DatePicker({
  label,
  value,
  onChange,
  onError,
  required,
  disabled = false,
  daysToDisable = [],
  thresholdDates = [],
  maxDate,
  minDate,
  disablePast = false,
  disabledDates = [],
  dataTestId,
}: DatePickerProps) {
  const [touched, setTouched] = useState(false)
  const [internalError, setInternalError] = useState<DateValidationError | null>(null)

  const CustomDay = useCallback(
    (pickersDayProps: PickersDayProps<Date>) => {
      const { day, ...other } = pickersDayProps
      const isThreshold = thresholdDates?.some(d => d.getTime() === day.getTime())

      return (
        <PickersDay
          {...other}
          day={day}
          sx={isThreshold ? { ...DAY_STYLES.threshold, ...DAY_STYLES.base } : DAY_STYLES.base}
        />
      )
    },
    [thresholdDates]
  )

  const shouldDisableDate = useCallback(
    (date: Date) => {
      const isThresholdDate = thresholdDates.some(d => d.getTime() === date.getTime())
      const isDisabledDate = disabledDates.some(d => date.toISOString() === new Date(d).toISOString())
      const isDisabledDay = daysToDisable.some(d => d === date.getDay())

      return isThresholdDate || isDisabledDate || isDisabledDay
    },
    [daysToDisable, disabledDates, thresholdDates]
  )

  const handleChange = (selectedDate: Date | null) => {
    setTouched(true)
    onChange(selectedDate)
  }

  const handleError = (newError: DateValidationError | null) => {
    setInternalError(newError)
    onError?.(newError)
  }

  const helperText = React.useMemo(() => {
    switch (internalError) {
      case 'maxDate':
        return maxDate ? `Please select a date before ${formatDateWithLocale(maxDate!)}` : INVALID_DATE_ERROR_MESSAGE
      case 'minDate':
        return minDate ? `Please select a date after ${formatDateWithLocale(minDate!)}` : INVALID_DATE_ERROR_MESSAGE
      case 'invalidDate':
        return INVALID_DATE_ERROR_MESSAGE
      default:
        return required ? 'Required' : ''
    }
  }, [internalError, minDate, maxDate, required])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
      <MuiDatePicker
        enableAccessibleFieldDOMStructure
        label={label}
        format={localeDateFormat}
        data-testid={dataTestId}
        value={value}
        onChange={handleChange}
        onError={handleError}
        disabled={disabled}
        autoFocus={false}
        maxDate={maxDate}
        minDate={minDate}
        disablePast={disablePast}
        shouldDisableDate={shouldDisableDate}
        slots={{ day: CustomDay }}
        slotProps={{
          actionBar: { actions: ['cancel', 'accept'] },
          textField: {
            fullWidth: true,
            helperText,
            error: !!internalError || (touched && required && !isValid(value)),
            autoComplete: 'off',
            inputProps: { 'data-testid': dataTestId },
          },
        }}
      />
    </LocalizationProvider>
  )
}

export default DatePicker
